import { useEffect } from "react";
import { DEBUG } from "../lib/debug.js";
import { followVillageLevels } from "../store/defenceLevels.js";
import { TabBar } from "../ui/components/TabBar.js";
import { DefenceEditor } from "../ui/screens/DefenceEditor.js";
import { Village } from "../ui/screens/Village.js";
import { WorldMap } from "../ui/screens/WorldMap.js";
import { AttackPlanner } from "../ui/screens/AttackPlanner.js";
import { useAttackStore } from "../store/attack.js";
import { Reports } from "../ui/screens/Reports.js";
import { Trials } from "../ui/screens/Trials.js";
import { Toast } from "../ui/components/Toast.js";
import { ReplayView } from "../ui/screens/ReplayView.js";
import { Tutorial } from "../ui/screens/Tutorial.js";
import { useUiStore } from "../store/ui.js";
import { useServerStore } from "../store/server.js";
import { useSaveStore } from "../store/save.js";
import { useSessionStore } from "../store/session.js";
import { useVillageStore } from "../store/village.js";
import { ScreenBoundary } from "../ui/components/ScreenBoundary.js";
import { InAppGate } from "../ui/components/InAppGate.js";
import { AccountSheet } from "../ui/components/AccountSheet.js";
import { EntryGate } from "../ui/screens/Welcome.js";
import {
  hasSeenTutorial,
  openSurveyIfDue,
  surveyAfterBattle,
  useTutorialStore,
} from "../store/tutorial.js";
import { SoundControl } from "../ui/components/SoundControl.js";
import { useGoalTicker } from "../store/goals.js";
import { playSound, unlockSound, silenceHiddenSound } from "../lib/sound.js";

export function App(): JSX.Element {
  useEffect(() => {
    const click = (event: MouseEvent): void => {
      if (!event.isTrusted) return;
      unlockSound();
      const button = (event.target as Element).closest?.("button");
      if (button && !button.disabled) playSound("tap");
    };
    const key = (event: KeyboardEvent): void => {
      if (event.isTrusted) unlockSound();
    };
    document.addEventListener("click", click);
    document.addEventListener("keydown", key);
    document.addEventListener("visibilitychange", silenceHiddenSound);
    return () => {
      document.removeEventListener("click", click);
      document.removeEventListener("keydown", key);
      document.removeEventListener("visibilitychange", silenceHiddenSound);
    };
  }, []);
  const tab = useUiStore((s) => s.tab);
  const villageView = useUiStore((s) => s.villageView);
  const checkHealth = useServerStore((s) => s.checkHealth);
  const connect = useSessionStore((s) => s.connect);
  const sessionStatus = useSessionStore((s) => s.status);
  const tutorialActive = useTutorialStore((s) => s.active);

  // Ask the browser not to clear this site's storage when space runs low: a
  // guest village lives there and nowhere else on this device (D-093).
  useEffect(() => {
    void navigator.storage?.persist?.().catch(() => false);
  }, []);

  // The defence editor's Wall and Guild follow the village's buildings (D-112).
  useEffect(() => followVillageLevels(), []);

  // Guide steps are recorded wherever they are finished, once, per village.
  useGoalTicker();

  useEffect(() => {
    void checkHealth();
    void connect();
  }, [checkHealth, connect]);

  // A stranger meets the game before they meet anything else, and before the
  // server has said anything.
  //
  // This used to wait for a session so that the survey at the end could be
  // attributed to a tester. The cost was that a first-time visitor sat in front
  // of an empty editor for the thirty to sixty seconds a sleeping free instance
  // takes to wake — the exact window in which somebody decides whether this is
  // a real game. The survey is still attributed when a session exists by the
  // time it is answered; a lost answer costs less than a lost first impression.
  //
  // Since D-117 the stranger first makes an account and founds a village; the
  // tutorial then starts as soon as that village is being played.
  const playingVillage = useSessionStore((s) => s.villageId);
  useEffect(() => {
    if (playingVillage && !hasSeenTutorial() && !useTutorialStore.getState().active) {
      useTutorialStore.getState().start();
    }
  }, [playingVillage]);

  // The Phase 0 survey is asked after the first real attack, back in the
  // village — not at the end of the tutorial, which no longer attacks.
  const attackOutcome = useAttackStore((s) => s.outcome);
  useEffect(() => {
    if (attackOutcome) surveyAfterBattle();
  }, [attackOutcome]);
  useEffect(() => {
    if (tab === "village" && !attackOutcome) openSurveyIfDue();
  }, [tab, attackOutcome]);

  // Pull the saved maze down once, and only once we actually have a session.
  // Chaining it off the status rather than firing both at mount is what keeps
  // the layout request from going out with no token and failing on its own.
  //
  // It also waits for the tutorial, which borrows the editor for its build
  // beats: a saved village landing in the middle of those would replace the
  // board under the player's finger, and the tutorial would then hand the
  // pre-tutorial board back over the top of it. The effect re-runs when the
  // tutorial closes, so the real maze arrives the moment it is wanted.
  useEffect(() => {
    if (sessionStatus !== "ready" || !playingVillage) return;
    if (tutorialActive) return;
    void useSaveStore.getState().load();
  }, [sessionStatus, tutorialActive, playingVillage]);

  // And the village, app-wide rather than on the village screen.
  //
  // Since D-109 an attack spends the units in the barracks, so the planner has
  // to know what is in there — and a player who opens the app on the map tab and
  // taps straight through to a target never visits the village screen at all.
  // Loading it here means the planner is never offering an army it has not been
  // told about; the village screen's own poll keeps it current while it is open.
  useEffect(() => {
    if (sessionStatus !== "ready") return;
    // Guest or saved (D-116), and which villages it owns (D-117).
    void useSessionStore.getState().loadAccount();
  }, [sessionStatus]);
  useEffect(() => {
    if (sessionStatus !== "ready" || !playingVillage) return;
    void useVillageStore.getState().load({ quiet: true });
  }, [sessionStatus, playingVillage]);

  return (
    <div className="flex h-full flex-col bg-bg">
      <Header />
      <Toast />
      <main className="min-h-0 flex-1">
        {/* Nothing of a village mounts until one is being played (D-117):
            the entry screens sit on top until then. */}
        {playingVillage ? (
          <ScreenBoundary key={tab}>
            {tab === "village" ? (
              villageView === "defence" ? (
                <DefenceEditor onBack={() => useUiStore.getState().setVillageView("village")} />
              ) : (
                <Village onOpenDefence={() => useUiStore.getState().setVillageView("defence")} />
              )
            ) : tab === "map" ? (
              <AttackTab />
            ) : tab === "trials" ? (
              <Trials />
            ) : (
              <Reports />
            )}
          </ScreenBoundary>
        ) : null}
      </main>
      <TabBar />
      {/* Over everything, including the tab bar: a replay is the one thing in
          this app you look at rather than operate. */}
      <ReplayView />
      <Tutorial />
      <EntryGate />
      <InAppGate />
      <AccountSheet />
    </div>
  );
}

function Header(): JSX.Element {
  const status = useServerStore((s) => s.status);
  const simVersion = useServerStore((s) => s.simVersion);
  const dot =
    status === "ok" ? "bg-ok" : status === "error" ? "bg-danger" : "bg-ink-dim animate-pulse";

  const username = useSessionStore((s) => s.username);
  const villageName = useSessionStore(
    (s) => s.villages?.find((v) => v.villageId === s.villageId)?.name ?? null
  );

  return (
    <header className="empire-header flex items-center justify-between gap-2 border-b border-panel-edge bg-bg-raised px-3">
      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="font-display text-base tracking-wide text-gold">EMPIRE</h1>
        {villageName ? (
          <span className="truncate text-xs text-ink-dim" data-testid="village-name">
            {villageName}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <SoundControl />
        {/* Players only need to hear about the server when it is unreachable. */}
        {status === "error" || DEBUG ? (
          <div
            className={`${status === "ok" ? "hidden sm:flex" : "flex"} items-center gap-2 text-xs text-ink-dim`}
          >
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            <span>
              {status === "ok" ? `sim ${simVersion}` : status === "error" ? "offline" : "…"}
            </span>
          </div>
        ) : null}
        {username ? (
          <button
            type="button"
            data-testid="header-log-out"
            onClick={() => useSessionStore.getState().logOut()}
            className="min-h-11 px-2 text-[11px] font-semibold text-ink-dim underline"
          >
            Log out
          </button>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Attack is one tab in two steps: pick a target, then plan the army. It used
 * to be two tabs (Map, then Army) that a player had to find and visit in order.
 */
function AttackTab(): JSX.Element {
  const target = useAttackStore((s) => s.confirming);
  const outcome = useAttackStore((s) => s.outcome);
  // Leaving the tab after a battle and coming back used to show the same
  // result screen again instead of the map. The result stays in Reports.
  useEffect(
    () => () => {
      if (useAttackStore.getState().outcome) useAttackStore.getState().leave();
    },
    []
  );
  return target || outcome ? <AttackPlanner /> : <WorldMap />;
}
